// User detail + role/active editor.

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { deactivateUser, getUser, updateUser } from '../../services/adminService';

const roleBadge = (r) => <span className={`badge badge-${r}`}>{r}</span>;

export default function AdminUserDetail() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = () => getUser(id).then(setUser).catch(e => setError(e?.response?.data?.error || e.message));
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) return <><div className="portal-content-header"><h1>User</h1></div><div className="portal-error">{error}</div></>;
  if (!user) return <><div className="portal-content-header"><h1>User</h1></div><div className="portal-empty">Loading…</div></>;

  const changeRole = async (newRole) => {
    setBusy(true);
    try { await updateUser(id, { role: newRole }); reload(); } finally { setBusy(false); }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      if (user.is_active) await deactivateUser(id);
      else await updateUser(id, { is_active: true });
      reload();
    } finally { setBusy(false); }
  };

  const toggleStaff = async () => {
    setBusy(true);
    try { await updateUser(id, { is_staff: !user.is_staff }); reload(); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="portal-content-header">
        <h1>{user.username} {roleBadge(user.role)}</h1>
        <Link to="/admin/users" className="portal-button secondary">← All users</Link>
      </div>

      <div className="portal-card">
        <div className="portal-defs">
          <Def label="Email" value={user.email || '—'} />
          <Def label="Display name" value={user.profile?.display_name || '—'} />
          <Def label="Auth method" value={user.profile?.auth_method || '—'} />
          <Def label="Joined" value={new Date(user.date_joined).toLocaleString()} />
          <Def label="Last login" value={user.last_login ? new Date(user.last_login).toLocaleString() : '—'} />
          <Def label="Location" value={[user.profile?.location_city, user.profile?.location_country].filter(Boolean).join(', ') || '—'} />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 'var(--tudu-space-5)', flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="portal-field-label" style={{ margin: 0 }}>Role:</span>
          {['customer', 'merchant', 'admin'].map(r => (
            <button
              key={r}
              type="button"
              className={`portal-button ${user.role === r ? '' : 'secondary'}`}
              disabled={busy}
              onClick={() => changeRole(r)}
            >{r}</button>
          ))}
          <span style={{ borderLeft: '1px solid var(--tudu-line-2)', height: 28, margin: '0 8px' }} />
          <button type="button" className={`portal-button ${user.is_active ? 'danger' : ''}`} disabled={busy} onClick={toggleActive}>
            {user.is_active ? 'Deactivate' : 'Reactivate'}
          </button>
          <button type="button" className="portal-button secondary" disabled={busy} onClick={toggleStaff}>
            {user.is_staff ? 'Remove staff flag' : 'Grant staff flag'}
          </button>
        </div>
      </div>

      <h3 className="portal-section-label">Owned stores</h3>
      {user.owned_stores?.length ? (
        <ul className="portal-card" style={{ listStyle: 'none', padding: 'var(--tudu-space-4)', margin: 0 }}>
          {user.owned_stores.map(s => (
            <li key={s.id} style={{ padding: '4px 0', color: 'var(--tudu-text)' }}>
              <Link to="/admin/stores" style={{ color: 'var(--tudu-accent-lavender)', fontWeight: 600 }}>{s.name}</Link>
              <span style={{ color: 'var(--tudu-text-lo)', marginLeft: 8 }}>#{s.id}</span>
            </li>
          ))}
        </ul>
      ) : <div className="portal-empty">No stores linked to this user.</div>}

      <h3 className="portal-section-label">Memberships</h3>
      {user.memberships?.length ? (
        <ul className="portal-card" style={{ listStyle: 'none', padding: 'var(--tudu-space-4)', margin: 0 }}>
          {user.memberships.map((m, i) => (
            <li key={i} style={{ padding: '4px 0', color: 'var(--tudu-text)' }}>
              {m.store} {m.tier && <span style={{ color: 'var(--tudu-text-lo)' }}>· {m.tier}</span>}
            </li>
          ))}
        </ul>
      ) : <div className="portal-empty">No loyalty memberships recorded.</div>}

      <h3 className="portal-section-label">Recent tasks</h3>
      {user.recent_tasks?.length ? (
        <ul className="portal-card" style={{ listStyle: 'none', padding: 'var(--tudu-space-4)', margin: 0 }}>
          {user.recent_tasks.map(t => (
            <li key={t.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--tudu-line-1)' }}>
              <span style={{ fontWeight: 500, color: 'var(--tudu-text-hi)' }}>{t.task_text}</span>
              <span style={{ color: 'var(--tudu-text-lo)', marginLeft: 8 }}>· {t.category}</span>
              {t.completed && <span className="badge badge-active" style={{ marginLeft: 8 }}>done</span>}
            </li>
          ))}
        </ul>
      ) : <div className="portal-empty">No recent tasks.</div>}
    </>
  );
}

function Def({ label, value }) {
  return (
    <div>
      <div className="portal-def-label">{label}</div>
      <div className="portal-def-value">{value}</div>
    </div>
  );
}
